// src/components/common/DynamicFAB.tsx
import React, { useState } from 'react';
import { useModal } from '../../contexts/ModalContext'; // 導入 useModal

interface DynamicFABProps {
  onClick: () => void;
  icon: React.ReactNode;
  color?: 'blue' | 'purple' | 'indigo';
  ariaLabel: string;
  className?: string;
}

/**
 * 動態浮動操作按鈕
 * 包含多層脈動光暈效果和點擊波浪動畫
 */
const DynamicFAB: React.FC<DynamicFABProps> = ({
  onClick,
  icon,
  color = 'blue',
  ariaLabel,
  className = '',
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const { isDayCloseModalOpen } = useModal(); // 使用 useModal hook

  // 如果日結模態視窗打開，不顯示 FAB
  if (isDayCloseModalOpen) {
    return null;
  }

  // 基本顏色映射
  const colorMap = {
    blue: 'bg-sky-600 hover:bg-sky-700',
    purple: 'bg-sky-500 hover:bg-sky-600',
    indigo: 'bg-sky-600 hover:bg-sky-700',
  };

  // 光暈顏色映射
  const glowColorMap = {
    blue: {
      outer: 'bg-sky-400/30',
      middle: 'bg-sky-500/40',
      inner: 'bg-sky-600/50'
    },
    purple: {
      outer: 'bg-sky-400/30',
      middle: 'bg-sky-500/40',
      inner: 'bg-sky-600/50'
    },
    indigo: {
      outer: 'bg-sky-400/30',
      middle: 'bg-sky-500/40',
      inner: 'bg-sky-600/50'
    },
  };

  const handleClick = () => {
    setIsAnimating(true);
    onClick();
    // 動畫結束後重置狀態
    setTimeout(() => setIsAnimating(false), 600);
  };

  return (
    <div className="relative group">
      {/* 外層光暈 - 始終可見但在懸停時更明顯 */}
      <div className={`
        absolute inset-0 rounded-full ${glowColorMap[color].outer}
        blur-xl scale-150 opacity-30 group-hover:opacity-70
        animate-glow-slow
      `}></div>
      
      {/* 中層光暈 - 懸停時顯示 */}
      <div className={`
        absolute inset-0 rounded-full ${glowColorMap[color].middle}
        blur-lg scale-125 opacity-0 group-hover:opacity-80
        animate-glow-medium
      `}></div>
      
      {/* 內層光暈 - 強調按鈕輪廓 */}
      <div className={`
        absolute inset-0 rounded-full ${glowColorMap[color].inner}
        blur-md scale-110 opacity-0 group-hover:opacity-90
        animate-glow-fast
      `}></div>
      
      <button
        onClick={handleClick}
        className={`
          w-14 h-14 rounded-full text-white shadow-lg flex items-center justify-center z-floating
          ${colorMap[color]} transition-all duration-300
          hover:shadow-xl hover:scale-105 active:scale-95
          relative overflow-hidden
          ${isAnimating ? 'animate-quick-pulse' : ''}
          ${className}
        `}
        aria-label={ariaLabel}
      >
        {/* 點擊波浪效果 */}
        {isAnimating && (
          <span className="absolute inset-0 rounded-full animate-ripple bg-white/30"></span>
        )}
        
        {/* 圖標 */}
        <div className="relative z-10">
          {icon}
        </div>
      </button>
    </div>
  );
};

export default DynamicFAB;